/**
 * Reply Queue tab body — renders the drafts-truth queue from `useReplyQueue`.
 *
 * Rows whose thread still exists in `os_conversation_inbox` are rendered with
 * the existing `ThreadListItem` component for visual consistency with the
 * rest of the inbox. Rows never re-open the inline draft/approve workspace
 * here (that workspace is keyed off the status-column-backed thread list,
 * which this tab deliberately bypasses) — instead every row, matched or
 * fallback, opens the lead's full dossier at `/leads/:heyreachLeadId`.
 *
 * Both row kinds navigate through the page-owned `onNavigate` callback
 * rather than calling `useNavigate()` directly here: the page's dirty-draft
 * guard only intercepts real `<a href>` clicks, so a bare `navigate()` call
 * from this panel would silently bypass it and could discard an unsaved
 * edit. Routing every row through one callback keeps that guard authoritative
 * no matter which row shape triggered the navigation.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, ChevronDown, Inbox as InboxIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ThreadListItem, type ThreadListItemThread } from '@/components/inbox/thread-list-item';
import {
  useDraftCampaignDomain,
  useReplyQueueThreadViews,
  type ReplyQueueDraftRow,
  type ReplyQueueThreadMetadata,
} from '@/hooks/useReplyQueue';
import { formatUiDateTime } from '@/lib/ui-format';
import { toSafeUiError } from '@/lib/user-safe-error';
import { cn } from '@/lib/utils';

export interface ReplyQueuePanelProps {
  rows: ReplyQueueDraftRow[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  archivedExcludedCount: number;
  includeArchived: boolean;
  onIncludeArchivedChange: (value: boolean) => void;
  onRetry: () => void;
  /** Page-owned guarded navigation — see file header. */
  onNavigate: (href: string) => void;
}

const formatQueueError = (error: unknown): string => {
  const safeError = toSafeUiError(error);
  return `${safeError.description} ${safeError.recovery}`;
};

/** `heyreach_lead_id` is unrestricted TEXT from the webhook — never interpolate it raw into a route. */
export function leadDossierHref(
  heyreachLeadId: string,
  selectedDraft?: Pick<ReplyQueueDraftRow, 'id' | 'thread_id'>
): string {
  const pathname = `/leads/${encodeURIComponent(heyreachLeadId)}`;
  if (!selectedDraft) return pathname;

  const search = new URLSearchParams({
    draft: selectedDraft.id,
    thread: selectedDraft.thread_id ?? '',
  });
  return `${pathname}?${search.toString()}`;
}

/**
 * Matched rows must render from the authoritative `ReplyQueueDraftRow`, not
 * the cached thread view: `useReplyQueueThreadViews`' query key is only the
 * sorted thread-id set, so a later inbound on the SAME thread (the webhook
 * reuses the thread and inserts a new draft row) changes nothing in that key
 * and the view never refetches. The view now supplies ONLY metadata the row
 * doesn't carry (stage, counts, sender seat) — lead identity, title, and the
 * message preview/timestamp/sender all come from the row, never the view.
 */
function toDisplayThread(
  meta: ReplyQueueThreadMetadata,
  row: ReplyQueueDraftRow,
  campaignName: string | undefined
): ThreadListItemThread {
  return {
    current_stage: meta.current_stage,
    message_count: meta.message_count,
    pending_draft_count: meta.pending_draft_count,
    sender_name: meta.sender_name,
    lead_name: row.lead_name,
    lead_title: row.lead_title,
    lead_company: row.lead_company,
    last_message_preview: row.incoming_message,
    last_message_at: row.incoming_at,
    // A queue row is definitionally an unanswered inbound — always theirs.
    last_message_sender: 'them',
    campaign_name: campaignName,
  };
}

function ReplyQueueCampaignBadge({
  campaignName,
  id,
}: {
  campaignName: string | undefined;
  id?: string;
}): JSX.Element {
  if (!campaignName) {
    return (
      <Badge
        id={id}
        variant="destructive"
        className="h-4 max-w-full truncate px-1 text-[10px]"
        data-testid="reply-queue-unresolved-campaign-badge"
        aria-label="Campaign name unresolved"
      >
        Campaign unresolved
      </Badge>
    );
  }

  return (
    <Badge
      id={id}
      variant="secondary"
      className="h-4 max-w-full truncate px-1 text-[10px]"
      data-content-origin="domain"
    >
      {campaignName}
    </Badge>
  );
}

/**
 * The queue row carries the full incoming message, unlike the ordinary
 * inbox's SQL-limited preview. The disclosure is a sibling of the dossier
 * action so its native button is never nested inside another button.
 *
 * It is intentionally shown for every row: whether line-clamp truncates is
 * responsive-layout dependent and cannot be inferred safely from length.
 */
function ReplyQueueMessageDisclosure({
  children,
  message,
}: {
  children: ReactNode;
  message: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-1">
      {children}
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-11 w-full justify-start px-3 text-xs text-muted-foreground md:min-h-11"
        >
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
            aria-hidden="true"
          />
          {open ? 'Hide full incoming message' : 'Show full incoming message'}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        <p
          className="whitespace-pre-wrap break-words text-xs text-muted-foreground"
          data-content-origin="external"
          data-testid="reply-queue-full-message"
        >
          {message}
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ReplyQueueFallbackRow({
  row,
  campaignName,
  onNavigate,
}: {
  row: ReplyQueueDraftRow;
  campaignName: string | undefined;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const openDossierLabelId = useId();
  const leadNameId = useId();
  const campaignDescriptionId = useId();

  return (
    <button
      type="button"
      onClick={() => onNavigate(leadDossierHref(row.heyreach_lead_id, row))}
      data-testid="reply-queue-fallback-row"
      aria-labelledby={`${openDossierLabelId} ${leadNameId}`}
      aria-describedby={campaignDescriptionId}
      className="block min-h-11 w-full rounded-xl border p-3 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span id={openDossierLabelId} className="sr-only">
        Open lead dossier for
      </span>
      <div className="flex items-center justify-between gap-2">
        <span
          id={leadNameId}
          className="truncate text-sm font-semibold"
          data-content-origin="external"
        >
          {row.lead_name}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatUiDateTime(row.incoming_at, {
            hour: '2-digit',
            minute: '2-digit',
            day: 'numeric',
            month: 'short',
          })}
        </span>
      </div>
      {row.lead_company && (
        <div className="text-xs text-muted-foreground" data-content-origin="external">
          {row.lead_company}
        </div>
      )}
      <div className="mt-1 flex">
        <ReplyQueueCampaignBadge
          id={campaignDescriptionId}
          campaignName={campaignName}
        />
      </div>
      <p className="mt-1 line-clamp-1 text-xs text-muted-foreground" data-content-origin="external">
        {row.incoming_message}
      </p>
    </button>
  );
}

function ReplyQueueThreadRow({
  row,
  thread,
  campaignName,
  onNavigate,
}: {
  row: ReplyQueueDraftRow;
  thread: ReplyQueueThreadMetadata;
  campaignName: string | undefined;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const campaignDescriptionId = useId();

  return (
    <ReplyQueueMessageDisclosure message={row.incoming_message}>
      <div className="space-y-1" data-testid="reply-queue-thread-row">
        <ThreadListItem
          thread={toDisplayThread(thread, row, campaignName)}
          isSelected={false}
          mode="dossier"
          campaignDescriptionId={campaignDescriptionId}
          onClick={() => onNavigate(leadDossierHref(row.heyreach_lead_id, row))}
        />
        {!campaignName && (
          <div className="flex px-3">
            <ReplyQueueCampaignBadge
              id={campaignDescriptionId}
              campaignName={undefined}
            />
          </div>
        )}
      </div>
    </ReplyQueueMessageDisclosure>
  );
}

export function ReplyQueuePanel({
  rows,
  isLoading,
  isError,
  error,
  archivedExcludedCount,
  includeArchived,
  onIncludeArchivedChange,
  onRetry,
  onNavigate,
}: ReplyQueuePanelProps): JSX.Element {
  const threadIds = rows
    .map((row) => row.thread_id)
    .filter((threadId): threadId is string => Boolean(threadId));
  const {
    data: threadViews,
    isLoading: threadViewsLoading,
    isError: threadViewsIsError,
    refetch: refetchThreadViews,
  } = useReplyQueueThreadViews(threadIds);
  const {
    data: draftCampaigns,
    isLoading: draftCampaignsLoading,
    isError: draftCampaignsIsError,
    error: draftCampaignsError,
    refetch: refetchDraftCampaigns,
  } = useDraftCampaignDomain();

  const nameByCampaign = useMemo(
    () => new Map(
      (draftCampaigns ?? []).map((campaign) => [
        campaign.heyreach_campaign_id,
        campaign.campaign_name,
      ])
    ),
    [draftCampaigns]
  );
  const unresolvedCampaignIds = useMemo(
    () => Array.from(new Set(
      rows
        .map((row) => row.heyreach_campaign_id)
        .filter((campaignId) => !nameByCampaign.has(campaignId))
    )).sort(),
    [nameByCampaign, rows]
  );
  const attemptedCampaignIdsRef = useRef<Set<string>>(new Set());
  const [reconcilingCampaignIds, setReconcilingCampaignIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // `draftCampaigns === undefined` also covers a DISABLED query — `useDraftCampaignDomain` is
  // gated on `(userId, organizationId)`, and a disabled TanStack query never leaves `undefined`.
  // So while auth is resolving, or for a user with no organization at all, this renders the queue's
  // loading state rather than rows.
  //
  // Dispositioned, not overlooked: transient during auth resolution is the tradeoff this slice
  // accepted deliberately (rows with blank badges would be indistinguishable from "this row has no
  // campaign", which is impossible — heyreach_campaign_id is NOT NULL). Permanent only for an
  // org-less authenticated user, which this product does not have: every user reaches the app
  // through an organization membership. If org-less users ever become real, this needs an explicit
  // empty state, not a skeleton.
  const domainPending = draftCampaignsLoading || draftCampaigns === undefined;

  useEffect(() => {
    if (domainPending || draftCampaignsIsError) return;

    const campaignIdsNeedingReconciliation = unresolvedCampaignIds.filter(
      (campaignId) => !attemptedCampaignIdsRef.current.has(campaignId)
    );
    if (campaignIdsNeedingReconciliation.length === 0) return;

    campaignIdsNeedingReconciliation.forEach((campaignId) => {
      attemptedCampaignIdsRef.current.add(campaignId);
    });
    setReconcilingCampaignIds((current) => {
      const next = new Set(current);
      campaignIdsNeedingReconciliation.forEach((campaignId) => next.add(campaignId));
      return next;
    });

    void refetchDraftCampaigns().finally(() => {
      setReconcilingCampaignIds((current) => {
        const next = new Set(current);
        campaignIdsNeedingReconciliation.forEach((campaignId) => next.delete(campaignId));
        return next;
      });
    });
  }, [domainPending, draftCampaignsIsError, refetchDraftCampaigns, unresolvedCampaignIds]);

  const unresolvedCampaignReconciliationPending = unresolvedCampaignIds.some(
    (campaignId) => (
      !attemptedCampaignIdsRef.current.has(campaignId) ||
      reconcilingCampaignIds.has(campaignId)
    )
  );
  const showLoading =
    isLoading ||
    threadViewsLoading ||
    domainPending ||
    unresolvedCampaignReconciliationPending;
  const showError = isError || draftCampaignsIsError;
  const shownError = isError ? error : draftCampaignsError;
  const retryBoth = (): void => {
    onRetry();
    void refetchThreadViews();
    void refetchDraftCampaigns();
  };

  return (
    <div data-testid="reply-queue-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Switch
            id="reply-queue-include-archived"
            checked={includeArchived}
            onCheckedChange={onIncludeArchivedChange}
            aria-describedby="reply-queue-hidden-count"
          />
          <Label htmlFor="reply-queue-include-archived" className="text-sm text-muted-foreground">
            Include archived contacts
          </Label>
        </div>
        {archivedExcludedCount > 0 && (
          <span
            id="reply-queue-hidden-count"
            className="text-xs text-muted-foreground"
          >
            {archivedExcludedCount} hidden by contact triage
          </span>
        )}
      </div>

      {showError ? (
        <div role="alert" className="p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <h3 className="mt-4 text-lg font-medium">Failed to load the reply queue</h3>
          <p className="mt-2 text-sm text-muted-foreground">{formatQueueError(shownError)}</p>
          <Button variant="outline" className="mt-4 min-h-11" onClick={retryBoth}>
            Retry
          </Button>
        </div>
      ) : showLoading ? (
        <div role="status" aria-label="Loading reply queue" className="space-y-3 p-4">
          <span className="sr-only">Loading reply queue…</span>
          {[1, 2, 3].map((item) => (
            <Card key={item} className="p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div role="status" aria-label="Reply queue all clear" className="p-8 text-center">
          <InboxIcon className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">Reply queue is empty</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            No leads have a live, unanswered draft right now.
          </p>
        </div>
      ) : (
        <>
          {threadViewsIsError && (
            <div
              role="status"
              className="flex flex-wrap items-center justify-between gap-2 border-b border-status-warning/40 bg-status-warning/10 px-4 py-2 text-xs text-status-warning"
            >
              <span>
                Some conversation details could not be loaded — the rows below are the true
                queue, shown with limited detail.
              </span>
              <Button variant="ghost" size="sm" className="h-11 shrink-0" onClick={retryBoth}>
                Retry
              </Button>
            </div>
          )}
          <div className="space-y-2 p-2">
            {rows.map((row) => {
              const thread =
                !threadViewsIsError && row.thread_id ? threadViews?.get(row.thread_id) : undefined;

              if (thread) {
                const campaignName = nameByCampaign.get(row.heyreach_campaign_id);
                return (
                  <ReplyQueueThreadRow
                    key={row.id}
                    row={row}
                    thread={thread}
                    campaignName={campaignName}
                    onNavigate={onNavigate}
                  />
                );
              }

              return (
                <ReplyQueueMessageDisclosure key={row.id} message={row.incoming_message}>
                  <ReplyQueueFallbackRow
                    row={row}
                    campaignName={nameByCampaign.get(row.heyreach_campaign_id)}
                    onNavigate={onNavigate}
                  />
                </ReplyQueueMessageDisclosure>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
